import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, errMsg, streamChat } from "@/lib/api";
import { labelOf, nextStep, parseAmount, summaryMessage } from "@/lib/agentFlow";

const Ctx = createContext(null);
export const useAgente = () => useContext(Ctx);
const getRef = () => new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("fb_ref") || undefined;
const INTENT_LABEL = { credito: "Quiero un crédito", seguros: "Quiero un seguro", inversiones: "Quiero invertir", franquicia: "Quiero una franquicia" };

export const AgenteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [profile, setProfile] = useState({});
  const [session, setSession] = useState({});
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [embeddedVisible, setEmbeddedVisible] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem("fiin_sid") || null);
  const embeddedRef = useRef(null);
  const profRef = useRef(profile);
  const resumeRef = useRef(false);
  profRef.current = profile;

  const ask = useCallback((p) => { const st = nextStep(p); setStep(st); if (st) setMsgs((m) => [...m, { role: "assistant", content: st.q, chips: st.chips, input: st.input }]); return st; }, []);
  const saveSid = (id) => { if (id && id !== sid) { setSid(id); localStorage.setItem("fiin_sid", id); } };
  const refreshSession = (id) => api.get(`/public/agent/session/${id}`).then(({ data }) => { setSession(data); setProfile((p) => ({ ...p, ...data.profile })); }).catch(() => {});

  const initRef = useRef(undefined);
  useEffect(() => {
    if (initRef.current === sid) return;
    initRef.current = sid;
    if (!sid) { ask({}); return; }
    Promise.all([api.get(`/public/agent/history/${sid}`), api.get(`/public/agent/session/${sid}`)]).then(([h, s]) => {
      setProfile(s.data.profile || {}); setSession(s.data);
      if (h.data.length) { setMsgs(h.data); setStep(null); } else ask(s.data.profile || {});
    }).catch(() => ask({}));
  }, [sid]); // eslint-disable-line

  const applyState = (acts) => acts.forEach((a) => { if (a.type === "update_profile" && a.profile) setProfile((p) => ({ ...p, ...a.profile })); if (a.type === "propuesta" && a.ok) setSession((s) => ({ ...s, sim_code: a.code })); if (a.type === "canalizar" && a.ok) setSession((s) => ({ ...s, lead_id: a.lead_id })); });
  const applyActions = (acts) => { setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], actions: [...(c[c.length - 1].actions || []), ...acts] }; return c; }); applyState(acts); };

  const sendLLM = async (text, p) => {
    setBusy(true); setStep(null);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamChat("/public/agent/chat", { message: text, session_id: sid, ref: getRef(), profile: p || profRef.current },
        (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }),
        (j) => { if (j.session_id) { saveSid(j.session_id); refreshSession(j.session_id); } }, applyActions);
    } catch (err) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `⚠️ ${err.message}` }; return c; });
    } finally { setBusy(false); if (resumeRef.current) { resumeRef.current = false; setTimeout(() => ask(profRef.current), 80); } }
  };

  const answer = (key, value, label) => {
    const p = { ...profRef.current };
    if (key === "intent") { if (value === "empresa") p.segment = "pm"; else p.vertical = value; } else p[key] = value;
    setProfile(p);
    setMsgs((m) => [...m, { role: "user", content: label }]);
    if (!ask(p)) sendLLM(summaryMessage(p), p);
  };

  const runAction = async (type, data) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/public/agent/actions", { session_id: sid, type, data });
      saveSid(r.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: { propuesta: "Listo, aquí tienes tu propuesta indicativa:", onboarding: "Perfecto, continúa tu solicitud en línea; toma unos minutos y puedes retomarla después:", canalizar: "¡Listo! Ya canalicé tu solicitud." }[type] || "Hecho.", actions: [r.result] }]);
      applyState([r.result]);
      if (type === "canalizar") setHandoff(false);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: `⚠️ ${errMsg(err)}` }]);
    } finally { setBusy(false); }
  };

  const submit = (text) => {
    if (!text || busy) return;
    if (step?.input === "amount") { const n = parseAmount(text); if (n) return answer("amount", n, text); }
    if (step?.input === "text") return answer(step.key, text, text);
    if (step?.key === "term_months") { const n = parseInt(text.replace(/\D/g, ""), 10); if (n > 0) return answer("term_months", /a[ñn]o/i.test(text) ? n * 12 : n, text); }
    if (step) resumeRef.current = true;
    sendLLM(text);
  };

  const reset = () => { localStorage.removeItem("fiin_sid"); initRef.current = null; setSid(null); setMsgs([]); setProfile({}); setSession({}); setHandoff(false); ask({}); };

  const startIntent = (d) => {
    if (!d.vertical && !d.plan) return;
    const p = { ...profRef.current, ...d };
    setProfile(p);
    const prodLabel = d.product && d.vertical !== "franquicia" ? labelOf(nextStep({ vertical: d.vertical, segment: d.segment || "pf" })?.chips, d.product) : null;
    setMsgs((m) => [...m, { role: "user", content: d.plan ? `Me interesa el plan ${labelOf(nextStep({ vertical: "franquicia" })?.chips, d.plan)}` : prodLabel ? `${INTENT_LABEL[d.vertical] || "Quiero asesoría"}: ${prodLabel}${d.segment === "pm" ? " (para mi empresa)" : ""}` : INTENT_LABEL[d.vertical] || "Quiero asesoría" }]);
    if (!ask(p)) sendLLM(summaryMessage(p), p);
  };

  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      startIntent(d);
      const el = embeddedRef.current;
      if (el && document.body.contains(el)) { el.scrollIntoView({ behavior: "smooth", block: "start" }); setOpen(false); } else setOpen(true);
    };
    window.addEventListener("fiin:open", h);
    return () => window.removeEventListener("fiin:open", h);
  }, []); // eslint-disable-line

  const value = { open, setOpen, msgs, profile, session, step, busy, handoff, setHandoff, answer, runAction, submit, reset, embeddedRef, embeddedVisible, setEmbeddedVisible };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
